import {
  ArrowDownToDot,
  ArrowUpFromDot,
  Check,
  ChevronDown,
  ChevronUp,
  Eraser,
  ListFilter,
  Plus,
  Settings2,
  ShoppingCart,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useInventory } from "../hooks/useInventory";
import { useShoppingList } from "../hooks/useShoppingList";
import {
  groupByCategory,
  loadSortPreference,
  type SortPreference,
  saveSortPreference,
  sortItems,
} from "../lib/sorting";
import type { ShoppingListItem } from "../services/shoppingListService";
import SearchInput from "./SearchInput";
import ShoppingListItemForm from "./ShoppingListItemForm";
import SortControl from "./SortControl";

// Component to manage and display the household shopping list, sync with realtime and provide actions
export default function ShoppingList() {
  const {
    items,
    isLoading,
    deleteItem,
    deleteItems,
    togglePurchased,
    addItem: addItemToShoppingList,
  } = useShoppingList();
  const { addItem: addItemToInventory } = useInventory();
  const [showAddForm, setShowAddForm] = useState(false);
  const [movingItemId, setMovingItemId] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
    const stored = localStorage.getItem("shoppingListFilters");
    return stored ? JSON.parse(stored) : [];
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [showShoppingControls, setShowShoppingControls] = useState(() => {
    const stored = localStorage.getItem("shoppingControlsExpanded");
    return stored === null ? true : stored === "true";
  });
  const [sort, setSort] = useState<SortPreference>(() =>
    loadSortPreference("shoppingSort"),
  );

  // Compute distinct categories present in the shopping list for filter UI
  const availableCategories = useMemo(() => {
    return [
      ...new Set(
        items.map((item) => item.category).filter((c): c is string => !!c),
      ),
    ].sort();
  }, [items]);

  // Derive filtered categories instead of using useEffect
  const filteredSelectedCategories = useMemo(() => {
    return selectedCategories.filter((cat) =>
      availableCategories.includes(cat),
    );
  }, [selectedCategories, availableCategories]);

  // Persist selected categories to localStorage
  useEffect(() => {
    localStorage.setItem(
      "shoppingListFilters",
      JSON.stringify(selectedCategories),
    );
  }, [selectedCategories]);

  // Persist selected sort preference to localStorage
  useEffect(() => {
    saveSortPreference("shoppingSort", sort);
  }, [sort]);

  // Persist expanded/collapsed state of the sort/filter/search controls
  useEffect(() => {
    localStorage.setItem(
      "shoppingControlsExpanded",
      String(showShoppingControls),
    );
  }, [showShoppingControls]);

  // Add item to shopping list and close add form
  const handleItemAdded = async (
    newItem: Omit<
      ShoppingListItem,
      "id" | "created_at" | "user_id" | "purchased" | "updated_at"
    >,
  ) => {
    try {
      await addItemToShoppingList({
        name: newItem.item_name,
        quantity: newItem.quantity,
        unit: newItem.unit,
        category: newItem.category ?? null,
      });
      setShowAddForm(false);
    } catch (error) {
      console.error("🔴 Failed to add item:", error);
    }
  };

  const handleDeleteItem = async (id: string) => {
    try {
      await deleteItem(id);
    } catch (error) {
      console.error("🔴 Failed to delete item:", error);
    }
  };

  const handleTogglePurchased = async (item: ShoppingListItem) => {
    try {
      await togglePurchased(item);
    } catch (error) {
      console.error("🔴 Failed to toggle purchased status:", error);
    }
  };

  const handleMoveToInventory = async (
    item: ShoppingListItem,
    location: "fridge" | "freezer" | "pantry" | "other",
  ) => {
    try {
      await addItemToInventory({
        item_name: item.item_name,
        quantity: item.quantity,
        unit: item.unit,
        category: item.category ?? null,
        location,
      });
      await deleteItem(item.id);
      const locationName = location.charAt(0).toUpperCase() + location.slice(1);
      toast.success(`"${item.item_name}" moved to ${locationName}`);
    } catch (error) {
      console.error("🔴 Failed to move item to inventory:", error);
      toast.error("Failed to move item to inventory. Please try again.");
    }
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const filteredItems = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return items.filter((item) => {
      const matchesCategory =
        filteredSelectedCategories.length === 0 ||
        (item.category && filteredSelectedCategories.includes(item.category));
      const matchesSearch =
        normalizedQuery === "" ||
        item.item_name.toLowerCase().includes(normalizedQuery);
      return matchesCategory && matchesSearch;
    });
  }, [items, filteredSelectedCategories, searchQuery]);

  const unpurchasedItems = filteredItems.filter((item) => !item.purchased);
  const purchasedItems = filteredItems.filter((item) => item.purchased);

  const sortedUnpurchasedItems = sortItems(unpurchasedItems, sort);
  const groupedUnpurchasedItems =
    sort.mode === "category" ? groupByCategory(unpurchasedItems) : null;

  const sortedPurchasedItems = sortItems(purchasedItems, sort);
  const groupedPurchasedItems =
    sort.mode === "category" ? groupByCategory(purchasedItems) : null;

  const handleRemoveAllPurchased = async () => {
    try {
      await deleteItems(purchasedItems.map((item) => item.id));
    } catch (error) {
      console.error("🔴 Failed to remove all purchased items:", error);
      toast.error("Failed to remove all purchased items. Please try again.");
    }
  };

  const renderUnpurchasedItem = (item: ShoppingListItem) => (
    <div
      key={item.id}
      className="rounded-lg bg-gray-50 p-4 shadow-sm hover:shadow-md dark:bg-gray-700"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="font-medium wrap-break-word text-gray-800 capitalize dark:text-gray-100">
            {item.item_name}
          </p>
          {sort.mode !== "category" && (
            <p className="text-xs text-gray-500 capitalize dark:text-gray-400">
              {item.category}
            </p>
          )}
          <p className="mt-2 text-sm font-semibold text-blue-600 dark:text-blue-400">
            {item.quantity} {item.unit}
          </p>
        </div>
        <div className="ml-4 flex gap-2 self-start">
          <button
            type="button"
            onClick={() => handleTogglePurchased(item)}
            className="shrink-0 rounded-lg p-2 text-green-600 hover:bg-green-100 dark:text-green-400 dark:hover:bg-gray-600"
            aria-label={`Mark ${item.item_name} as purchased`}
          >
            <Check className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() =>
              setMovingItemId(movingItemId === item.id ? null : item.id)
            }
            className="shrink-0 rounded-lg p-2 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-600"
            aria-label={`Move ${item.item_name} to inventory`}
            aria-expanded={movingItemId === item.id}
          >
            {movingItemId === item.id ? (
              <ArrowUpFromDot className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ArrowDownToDot className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            onClick={() => handleDeleteItem(item.id)}
            className="shrink-0 rounded-lg p-2 text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-gray-600"
            aria-label={`Delete ${item.item_name}`}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
      {movingItemId === item.id && (
        <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-600">
          <p className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-400">
            Move to:
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleMoveToInventory(item, "fridge")}
              className="flex-1 rounded-lg bg-blue-100 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-200 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-700"
            >
              Fridge
            </button>
            <button
              type="button"
              onClick={() => handleMoveToInventory(item, "freezer")}
              className="flex-1 rounded-lg bg-cyan-100 px-3 py-2 text-sm font-medium text-cyan-700 hover:bg-cyan-200 dark:bg-cyan-600 dark:text-white dark:hover:bg-cyan-700"
            >
              Freezer
            </button>
            <button
              type="button"
              onClick={() => handleMoveToInventory(item, "pantry")}
              className="flex-1 rounded-lg bg-yellow-100 px-3 py-2 text-sm font-medium text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-600 dark:text-white dark:hover:bg-yellow-700"
            >
              Pantry
            </button>
            <button
              type="button"
              onClick={() => handleMoveToInventory(item, "other")}
              className="flex-1 rounded-lg bg-purple-100 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-200 dark:bg-purple-600 dark:text-white dark:hover:bg-purple-700"
            >
              Other
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderPurchasedItem = (item: ShoppingListItem) => (
    <div
      key={item.id}
      className="rounded-lg bg-gray-50 p-4 hover:shadow-md dark:bg-gray-700"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <p className="font-medium wrap-break-word text-gray-800 line-through dark:text-gray-100">
            {item.item_name}
          </p>
          {sort.mode !== "category" && item.category && (
            <p className="text-xs text-gray-500 capitalize dark:text-gray-400">
              {item.category}
            </p>
          )}
          <p className="mt-2 text-sm font-semibold text-blue-600 dark:text-blue-400">
            {item.quantity} {item.unit}
          </p>
        </div>
        <div className="ml-4 flex gap-2">
          <button
            type="button"
            onClick={() => handleTogglePurchased(item)}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-600"
            aria-label={`Mark ${item.item_name} as not purchased`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() =>
              setMovingItemId(movingItemId === item.id ? null : item.id)
            }
            className="rounded-lg p-2 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-gray-600"
            aria-label={`Move ${item.item_name} to inventory`}
            aria-expanded={movingItemId === item.id}
          >
            {movingItemId === item.id ? (
              <ArrowUpFromDot className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ArrowDownToDot className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            onClick={() => handleDeleteItem(item.id)}
            className="rounded-lg p-2 text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-gray-600"
            aria-label={`Delete ${item.item_name}`}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
      {movingItemId === item.id && (
        <div className="mt-3 border-t border-gray-200 pt-3 dark:border-gray-600">
          <p className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-400">
            Move to:
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleMoveToInventory(item, "fridge")}
              className="flex-1 rounded-lg bg-blue-100 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-200 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-700"
            >
              Fridge
            </button>
            <button
              type="button"
              onClick={() => handleMoveToInventory(item, "freezer")}
              className="flex-1 rounded-lg bg-cyan-100 px-3 py-2 text-sm font-medium text-cyan-700 hover:bg-cyan-200 dark:bg-cyan-600 dark:text-white dark:hover:bg-cyan-700"
            >
              Freezer
            </button>
            <button
              type="button"
              onClick={() => handleMoveToInventory(item, "pantry")}
              className="flex-1 rounded-lg bg-yellow-100 px-3 py-2 text-sm font-medium text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-600 dark:text-white dark:hover:bg-yellow-700"
            >
              Pantry
            </button>
            <button
              type="button"
              onClick={() => handleMoveToInventory(item, "other")}
              className="flex-1 rounded-lg bg-purple-100 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-200 dark:bg-purple-600 dark:text-white dark:hover:bg-purple-700"
            >
              Other
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <output className="sr-only" aria-live="polite" aria-atomic="true">
        {isLoading ? "Loading shopping list..." : ""}
        {!isLoading && unpurchasedItems.length > 0
          ? `${unpurchasedItems.length} item${unpurchasedItems.length !== 1 ? "s" : ""} to buy`
          : ""}
      </output>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
            Shopping List
          </h2>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Keep track of items you need to buy
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="mt-4 flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white shadow-md hover:bg-blue-700 hover:shadow-lg focus-visible:shadow-[0_0_0_2px_rgb(var(--color-bg-primary)),0_0_0_4px_rgb(59_130_246)] dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus-visible:shadow-[0_0_0_2px_rgb(31_41_55),0_0_0_4px_rgb(59_130_246)]"
        >
          <Plus className="h-5 w-5" />
          Add Item
        </button>
      </div>

      {showAddForm && (
        <ShoppingListItemForm
          onClose={() => setShowAddForm(false)}
          onItemAdded={handleItemAdded}
        />
      )}

      {items.length > 0 && (
        <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-xl font-semibold text-gray-800 dark:text-gray-100">
              <Settings2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              Item Controls
            </h3>
            <button
              type="button"
              onClick={() => setShowShoppingControls((v) => !v)}
              className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              aria-expanded={showShoppingControls}
              aria-controls="shopping-controls-panel"
              aria-label={
                showShoppingControls
                  ? "Collapse sort, filter, and search options"
                  : "Expand sort, filter, and search options"
              }
            >
              {showShoppingControls ? (
                <ChevronUp className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>

          {showShoppingControls && (
            <div id="shopping-controls-panel" className="mt-6 space-y-6">
              <SortControl
                value={sort}
                onChange={setSort}
                legend="Shopping list sort order"
              />

              {availableCategories.length > 0 && (
                <fieldset className="border-t border-gray-200 pt-6 dark:border-gray-700">
                  <legend className="sr-only">Category Filter</legend>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-md flex items-center gap-2 font-bold text-gray-800 dark:text-gray-100">
                      <ListFilter className="h-4 w-4 text-blue-500 dark:text-blue-400" />
                      Filter:
                    </h3>
                    <button
                      type="button"
                      onClick={() => setSelectedCategories([])}
                      className={`flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium ${
                        filteredSelectedCategories.length > 0
                          ? "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                          : "pointer-events-none invisible opacity-0"
                      }`}
                      aria-label="Clear all filters"
                    >
                      <Eraser className="h-4 w-4" aria-hidden="true" />
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {availableCategories.map((cat) => (
                      <button
                        type="button"
                        key={cat}
                        onClick={() => toggleCategory(cat)}
                        aria-pressed={filteredSelectedCategories.includes(cat)}
                        className={`rounded-full px-3 py-1 text-sm font-medium capitalize ${
                          filteredSelectedCategories.includes(cat)
                            ? "bg-blue-600 text-white dark:bg-blue-500"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                        }`}
                      >
                        {cat.replace("_", " ")}
                      </button>
                    ))}
                  </div>
                </fieldset>
              )}

              <SearchInput
                id="shopping-search"
                label="Search shopping list items"
                placeholder="Search items..."
                value={searchQuery}
                onChange={setSearchQuery}
              />
            </div>
          )}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
          <h3 className="mb-4 flex items-center gap-2 text-xl font-bold text-gray-800 dark:text-gray-100">
            <ShoppingCart className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            To Buy ({unpurchasedItems.length})
          </h3>
          <div className="space-y-3">
            {isLoading ? (
              <output
                className="flex items-center justify-center py-7"
                aria-live="polite"
              >
                <div
                  className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600 dark:border-blue-700 dark:border-t-blue-400"
                  aria-hidden="true"
                ></div>
                <span className="sr-only">Loading shopping list items...</span>
              </output>
            ) : unpurchasedItems.length === 0 ? (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                <p>
                  No items to buy
                  {selectedCategories.length > 0 || searchQuery.trim() !== ""
                    ? " match your filters"
                    : ""}
                </p>
              </div>
            ) : groupedUnpurchasedItems ? (
              <div className="space-y-5">
                {groupedUnpurchasedItems.map((group) => (
                  <div key={group.category}>
                    <h4 className="mb-2 text-xs font-bold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                      {group.category.replace("_", " ")}
                    </h4>
                    <div className="space-y-3">
                      {group.items.map((item) => renderUnpurchasedItem(item))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              sortedUnpurchasedItems.map((item) => renderUnpurchasedItem(item))
            )}
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow-sm dark:bg-gray-800">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-xl font-bold text-gray-800 dark:text-gray-100">
              <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
              Purchased ({purchasedItems.length})
            </h3>
            <button
              type="button"
              onClick={handleRemoveAllPurchased}
              className={`flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium ${
                purchasedItems.length > 0
                  ? "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                  : "pointer-events-none invisible opacity-0"
              }`}
              aria-label="Remove all purchased items"
            >
              <Eraser className="h-4 w-4" aria-hidden="true" />
              Remove All
            </button>
          </div>
          <div className="space-y-4">
            {isLoading ? (
              <output
                className="flex items-center justify-center py-7"
                aria-live="polite"
              >
                <div
                  className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600 dark:border-blue-700 dark:border-t-blue-400"
                  aria-hidden="true"
                ></div>
                <span className="sr-only">Loading purchased items...</span>
              </output>
            ) : purchasedItems.length === 0 ? (
              <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                <p>
                  No purchased items
                  {selectedCategories.length > 0 || searchQuery.trim() !== ""
                    ? " match your filters"
                    : ""}
                </p>
              </div>
            ) : groupedPurchasedItems ? (
              <div className="space-y-5">
                {groupedPurchasedItems.map((group) => (
                  <div key={group.category}>
                    <h4 className="mb-2 text-xs font-bold tracking-wide text-gray-500 uppercase dark:text-gray-400">
                      {group.category.replace("_", " ")}
                    </h4>
                    <div className="space-y-4">
                      {group.items.map((item) => renderPurchasedItem(item))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              sortedPurchasedItems.map((item) => renderPurchasedItem(item))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
